/**
 * OpenPanel SDK singleton — the ONLY file allowed to import '@openpanel/web'
 * (ESLint no-restricted-imports enforces this everywhere else; ADR-003).
 */
import { OpenPanel } from "@openpanel/web";
import { analyticsEnv } from "./env";
import { deepContainsPhiKey } from "./phi";

/** Minimal SDK surface the facade depends on (verified against @openpanel/web@1.4.1 .d.ts). */
export interface AnalyticsClient {
  track(name: string, properties?: Record<string, unknown>): unknown;
  identify(payload: {
    profileId: string;
    properties?: Record<string, unknown>;
  }): unknown;
  setGlobalProperties(properties: Record<string, unknown>): void;
  clear(): void;
  /** Flushes the SDK's native `disabled: true` pre-identify queue. */
  ready(): void;
}

/**
 * Final PHI backstop applied at the SDK boundary (defense-in-depth layer 5):
 * returns false (drop event) if any CALLER-supplied property key looks like PHI.
 *
 * The SDK hands us the full envelope — `{ type, payload: { name, profileId,
 * properties } }` — where `name` is the registry-fixed event name (e.g.
 * `"login_succeeded"`) and would itself false-positive on the `/name/` denylist,
 * silently dropping every track event. Only `payload.properties` holds
 * caller-supplied data, so scan exactly that (falling back to a bare
 * `properties` object for direct callers), never the SDK's structural keys.
 */
export function phiBackstopFilter(event: unknown): boolean {
  const e = event as {
    type?: string;
    payload?: { properties?: unknown };
    properties?: unknown;
  };
  // Track-family payloads carry the structural `name` key (the registry event
  // name), which false-positives the /name/ denylist — scan only their
  // caller-supplied `properties` (the fd750ed P0). Every OTHER payload type
  // has no denylisted structural keys, and identify is exactly where PII
  // (email/firstName/lastName exist on the SDK type) could ride at the TOP
  // level of the payload — scan those payloads whole (review MAJOR: the
  // narrowed scan had traded this coverage away).
  const isTrackShaped =
    e?.type === "track" || e?.type === "increment" || e?.type === "decrement";
  if (isTrackShaped) {
    return !deepContainsPhiKey(e?.payload?.properties);
  }
  if (e?.payload !== undefined) {
    return !deepContainsPhiKey(e.payload);
  }
  return !deepContainsPhiKey(e?.properties);
}

/** `undefined` = not yet initialised; `null` = kill switch (env unset). */
let cached: AnalyticsClient | null | undefined;

/**
 * Lazy module-scope singleton (StrictMode-safe — never created in an effect).
 * Returns null when analyticsEnv is null (kill switch: unset Client ID).
 */
export function getClient(): AnalyticsClient | null {
  if (cached !== undefined) {
    return cached;
  }
  if (analyticsEnv === null) {
    cached = null;
    return cached;
  }
  cached = new OpenPanel({
    clientId: analyticsEnv.clientId,
    apiUrl: analyticsEnv.apiUrl,
    // Auto-capture fully OFF — never scrape on-screen PHI (US-A1).
    trackScreenViews: false,
    trackOutgoingLinks: false,
    trackAttributes: false,
    trackHashChanges: false,
    // Queue events natively until identify()+ready() (pre-identify tenant safety).
    disabled: true,
    // Layer-5 PHI backstop at the SDK boundary.
    filter: phiBackstopFilter,
  }) as AnalyticsClient;
  return cached;
}
